import { Fragment, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { keyframes, useTheme } from '@emotion/react'
import clsx from 'clsx'
import useWindowScroll from 'react-use/lib/useWindowScroll'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'

import DesktopNav from './DesktopNav'
import MobileNav from './MobileNav'
import { StylesHeaderVariables } from './UI'

import Link from '~components/Link'
import Logo from '~components/Logo'

const headerEntrance = keyframes({
  from: {
    transform: 'translate3d(0, -100%, 0)',
  },
  to: {
    transform: 'translateZ(0)',
  },
})

const Header = ({
  contrast = true,
  forceCTAContrast,
  hasDefaultOverlay = false,
  showNav = true,
  showPrimaryLogo,
  sticky = true,
  showCTA,
  logoAside,
}) => {
  const theme = useTheme()

  const headerRef = useRef()

  const { y } = useWindowScroll()

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isStuck, setIsStuck] = useState(false)
  const offset = 300

  useEffect(() => {
    // Only recalculate when we pass through the `sticky` prop
    if (!sticky) return

    if (!isStuck && y >= offset) {
      setIsStuck(true)
    } else if (isStuck && y < offset) {
      setIsStuck(false)
    }
  }, [y, isStuck, sticky])

  // Make sure we kill mobile nav styles between routes
  const handleNavClick = () => {
    if (isMobileNavOpen) {
      setIsMobileNavOpen(false)
    }
  }

  return (
    <Fragment>
      <header
        css={[
          {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: StylesHeaderVariables(theme).height,
            zIndex: 10,
            display: 'grid',
            alignContent: 'center',
          },

          hasDefaultOverlay && {
            background: theme.color.dark.overlay,
          },

          isStuck && {
            position: 'fixed',
            background: theme.color.dark.overlay,
            animation: `${headerEntrance} ${theme.timing.base}`,
          },
        ]}
        className={clsx('Global__Header', {
          'is-sticky': isStuck,
        })} // @NOTE Requirement for Mutiny to have className they can query off of for interaction
        ref={headerRef}
      >
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: theme.space.small,
            alignItems: 'center',
            maxWidth: StylesContainerVariables.base,
            margin: '0 auto',
            width: '100%',
            paddingLeft: theme.space.base,
            paddingRight: theme.space.base,
          }}
          className={clsx({
            [theme.settings.classes.contrast]: contrast,
          })}
        >
          <div
            css={{
              display: 'grid',
              gridAutoColumns: 'auto',
              gridAutoFlow: 'column',
              gap: logoAside && theme.space.base,
              alignItems: 'end',
            }}
          >
            <Link
              to="/"
              css={{
                width: StylesHeaderVariables(theme).logoWidth,
                display: 'block',
              }}
              title="Gremlin"
              onClick={() => handleNavClick()}
            >
              {/* eslint-disable no-nested-ternary */}
              <Logo
                variation={
                  !contrast && !isStuck
                    ? showPrimaryLogo
                      ? 'green'
                      : 'black'
                    : 'white'
                }
              />
              {/* eslint-enable no-nested-ternary */}
            </Link>
            {logoAside}
          </div>
          {showNav && (
            <Fragment>
              <DesktopNav
                contrast={contrast}
                isStuck={isStuck}
                forceCTAContrast={forceCTAContrast}
                showCTA={showCTA}
              />
              <MobileNav contrast={contrast} isStuck={isStuck} />
            </Fragment>
          )}
        </div>
      </header>
    </Fragment>
  )
}

Header.propTypes = {
  contrast: PropTypes.bool,
  forceCTAContrast: PropTypes.bool,
  hasDefaultOverlay: PropTypes.bool,
  sticky: PropTypes.bool,
  showNav: PropTypes.bool,
  showPrimaryLogo: PropTypes.bool,
  showCTA: PropTypes.bool,
  logoAside: PropTypes.any,
}

export default Header
